import { useState, useMemo, useRef } from 'react'
import MapboxMap, {
  FullscreenControl,
  GeolocateControl,
  Marker,
  NavigationControl
} from 'react-map-gl'

import { useQuery } from '@tanstack/react-query'

import api from 'services/api'
import plannedSternaPin from 'assets/plannedSternaPin'

import { ModelCity } from 'models/ModelCity'
import { findDestinationCatalogState } from 'utils/destinationCatalog'

import CityModal from 'components/cityModal'

import * as C from './styles'

type CityMarkerCandidate = {
  key: string
  city: ModelCity
  itineraryCount: number
  contentScore: number
  markerStatus: 'completed' | 'inspire'
  destinationSlug?: string | null
}

const normalizeCityName = (name = '') =>
  name
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .trim()
    .toLowerCase()

const MapPage = (props: any) => {
  const mapHeight = props.height ?? (window.innerWidth < 800 ? 520 : 680)
  const [selectedCity, setSelectedCity] = useState<ModelCity | null>()
  const [isOpen, setIsOpen] = useState(false)
  const [mapReady, setMapReady] = useState(false)
  const [zoom, setZoom] = useState(4)
  const mapRef = useRef<any>()

  const { data: mapMarkers = [] } = useQuery({
    queryKey: ['MapMarkers'],
    queryFn: async () => {
      const { data } = await api.get('/itineraries/map-markers')
      return data ?? []
    },
    staleTime: 0,
    refetchOnMount: 'always'
  })

  const { data: destinationCatalogState = [] } = useQuery({
    queryKey: ['destination-catalog-state'],
    queryFn: async () => {
      const { data } = await api.get('/cities/catalog-state')
      return data ?? []
    },
    staleTime: 5 * 60 * 1000
  })

  const minimumZoom = window.innerWidth < 800 ? 1 : 2

  const cityCandidates = useMemo<CityMarkerCandidate[]>(() => {
    return mapMarkers
      .map((marker: any) => {
        const latitude = Number(marker.latitude)
        const longitude = Number(marker.longitude)
        const name = String(marker.name || '')

        return {
          key: `${normalizeCityName(name)}|${latitude.toFixed(
            3
          )}|${longitude.toFixed(3)}`,
          city: {
            id: marker.id,
            name,
            latitude,
            longitude
          } as ModelCity,
          itineraryCount: Number(marker.itineraryCount || 0),
          contentScore: Number(marker.contentScore || 0),
          markerStatus:
            marker.markerStatus === 'completed' ? 'completed' : 'inspire',
          destinationSlug: marker.destinationSlug || null
        }
      })
      .filter(
        (candidate: CityMarkerCandidate) =>
          Number.isFinite(Number(candidate.city.latitude)) &&
          Number.isFinite(Number(candidate.city.longitude))
      )
      .sort((a: CityMarkerCandidate, b: CityMarkerCandidate) => {
        if (a.markerStatus !== b.markerStatus) {
          return a.markerStatus === 'completed' ? -1 : 1
        }

        if (b.itineraryCount !== a.itineraryCount) {
          return b.itineraryCount - a.itineraryCount
        }

        if (b.contentScore !== a.contentScore) {
          return b.contentScore - a.contentScore
        }

        return a.city.name.localeCompare(b.city.name, 'pt-BR')
      })
  }, [mapMarkers])

  const visibleCities = useMemo(() => {
    if (!mapReady || !mapRef.current) return cityCandidates

    const accepted: Array<{
      candidate: CityMarkerCandidate
      x: number
      y: number
    }> = []

    // Mantém os pins visualmente separados. Como a distância em pixels entre
    // cidades aumenta naturalmente durante o zoom, pins secundários surgem
    // progressivamente sem precisar de clusters ou contadores.
    const minimumPixelDistance = zoom >= 8 ? 26 : zoom >= 6 ? 30 : 36

    cityCandidates.forEach((candidate) => {
      const point = mapRef.current.project([
        Number(candidate.city.longitude),
        Number(candidate.city.latitude)
      ])

      const overlaps = accepted.some(({ x, y }) => {
        const dx = point.x - x
        const dy = point.y - y
        return Math.sqrt(dx * dx + dy * dy) < minimumPixelDistance
      })

      if (!overlaps) {
        accepted.push({ candidate, x: point.x, y: point.y })
      }
    })

    return accepted.map(({ candidate }) => candidate)
  }, [cityCandidates, mapReady, zoom])

  return (
    <C.Container>
      <MapboxMap
        style={{ width: '100%', height: mapHeight }}
        mapboxAccessToken="pk.eyJ1IjoibGF1cmFoZGV2IiwiYSI6ImNsY3BtMTN4dDFueG0zcG81em41YXh4emIifQ.eGKakvFNW6mcmRuHze23mg"
        mapStyle="mapbox://styles/mapbox/streets-v9"
        initialViewState={{ longitude: 12, latitude: 40, zoom: 4 }}
        minZoom={minimumZoom}
        renderWorldCopies={false}
        ref={mapRef}
        onLoad={() => setMapReady(true)}
        onMove={(event: any) => setZoom(event.viewState.zoom)}
      >
        <GeolocateControl position="top-left" />
        <FullscreenControl position="top-left" />
        <NavigationControl position="top-left" />

        {visibleCities.map(({ key, city, markerStatus, destinationSlug }) => {
          const destinationState = destinationSlug
            ? undefined
            : findDestinationCatalogState(destinationCatalogState, city)
          const resolvedDestinationSlug =
            destinationSlug ||
            (destinationState?.status !== 'hidden'
              ? destinationState?.slug
              : undefined)

          const pinImage = (
            <img
              src={
                markerStatus === 'completed'
                  ? '/images/pin.png'
                  : plannedSternaPin
              }
              alt=""
              aria-hidden="true"
              height={25}
              width={23}
            />
          )

          return (
            <Marker
              key={key}
              longitude={Number(city.longitude)}
              latitude={Number(city.latitude)}
              anchor="bottom"
              style={{ zIndex: 20, pointerEvents: 'auto' }}
            >
              {resolvedDestinationSlug ? (
                <C.MarkerLink
                  href={`/destinos/${resolvedDestinationSlug}`}
                  aria-label={`Abrir ficha de ${city.name}`}
                >
                  {pinImage}
                </C.MarkerLink>
              ) : (
                <C.IconButton
                  type="button"
                  aria-label={`Abrir ficha de ${city.name}`}
                  onClick={(event) => {
                    event.stopPropagation()
                    setSelectedCity(city)
                    setIsOpen(true)
                  }}
                >
                  {pinImage}
                </C.IconButton>
              )}
            </Marker>
          )
        })}
        <C.MapLegend>
          <span>
            <img src="/images/pin.png" alt="" />
            Viagem realizada
          </span>
          <span>
            <img src={plannedSternaPin} alt="" />
            Para inspirar
          </span>
        </C.MapLegend>
      </MapboxMap>
      {selectedCity && (
        <CityModal
          isOpen={isOpen}
          city={selectedCity}
          closeModal={() => setIsOpen(false)}
        />
      )}
    </C.Container>
  )
}

export default MapPage
